import React, { useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { OnboardDeviceOnlinePeer, OnboardDevicesOnlinePeersResponse } from '../interfaces';

interface DataGridComponentProps {
    peers: OnboardDevicesOnlinePeersResponse;
}

interface MergedPeerRow {
    id: string;
    main: OnboardDeviceOnlinePeer | null;
    secondary: OnboardDeviceOnlinePeer | null;
}

const EMPTY_VALUE = '-';
const KYIV_DATETIME_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
});

const getPeerStableKey = (peer: OnboardDeviceOnlinePeer): string => {
    if (peer.deviceId) {
        return `deviceId:${peer.deviceId}`;
    }

    if (peer.pubkey) {
        return `pubkey:${peer.pubkey}`;
    }

    if (peer.name) {
        return `name:${peer.name}`;
    }

    if (peer.endpoint) {
        return `endpoint:${peer.endpoint}`;
    }

    return 'unknown';
};

const getUniqueRowKey = (
    rowsByKey: Map<string, MergedPeerRow>,
    peer: OnboardDeviceOnlinePeer,
    serverName: 'main' | 'secondary',
    index: number,
): string => {
    const baseKey = getPeerStableKey(peer);
    const fallbackKey = `${serverName}:${index}`;

    const key = baseKey === 'unknown' ? fallbackKey : baseKey;
    const existing = rowsByKey.get(key);

    if (!existing) {
        return key;
    }

    if (serverName === 'main' && !existing.main) {
        return key;
    }

    if (serverName === 'secondary' && !existing.secondary) {
        return key;
    }

    return `${key}#${serverName}-${index}`;
};

const mergePeers = (
    mainPeers: OnboardDeviceOnlinePeer[],
    secondaryPeers: OnboardDeviceOnlinePeer[],
): MergedPeerRow[] => {
    const rowsByKey = new Map<string, MergedPeerRow>();

    mainPeers.forEach((peer, index) => {
        const key = getUniqueRowKey(rowsByKey, peer, 'main', index);
        const existing = rowsByKey.get(key);

        if (existing) {
            existing.main = peer;
            return;
        }

        rowsByKey.set(key, {
            id: key,
            main: peer,
            secondary: null,
        });
    });

    secondaryPeers.forEach((peer, index) => {
        const key = getUniqueRowKey(rowsByKey, peer, 'secondary', index);
        const existing = rowsByKey.get(key);

        if (existing) {
            existing.secondary = peer;
            return;
        }

        rowsByKey.set(key, {
            id: key,
            main: null,
            secondary: peer,
        });
    });

    return Array.from(rowsByKey.values());
};

const formatText = (value?: string | null): string => {
    if (!value) {
        return EMPTY_VALUE;
    }

    return value;
};

const formatBoolean = (value?: boolean): string => {
    if (value === undefined || value === null) {
        return EMPTY_VALUE;
    }

    return value ? 'True' : 'False';
};

const formatAllowedIps = (value?: string[]): string => {
    if (!Array.isArray(value) || value.length === 0) {
        return EMPTY_VALUE;
    }

    return value.join(', ');
};

const formatOnlineSinceKyiv = (value?: string | null): string => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return EMPTY_VALUE;
    }

    return KYIV_DATETIME_FORMATTER.format(date);
};

const formatOnlineTime = (value?: number): string => {
    if (value === undefined || value === null || value < 0) {
        return EMPTY_VALUE;
    }

    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatBytes = (value?: number): string => {
    if (value === undefined || value === null || value < 0) {
        return EMPTY_VALUE;
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let amount = value;
    let unitIndex = 0;

    while (amount >= 1024 && unitIndex < units.length - 1) {
        amount /= 1024;
        unitIndex += 1;
    }

    return `${amount.toFixed(2)} ${units[unitIndex]}`;
};

const formatTraffic = (rawBytesValue?: number, humanValue?: string): string => {
    const formattedRawBytes = formatBytes(rawBytesValue);

    if (formattedRawBytes !== EMPTY_VALUE) {
        return formattedRawBytes;
    }

    return formatText(humanValue);
};

export const OnboardDevicesOnlineTableGrid = ({ peers }: DataGridComponentProps) => {
    const columns: GridColDef[] = [
        { field: 'device_id', headerName: 'Device Id', width: 180 },
        { field: 'name', headerName: 'Name', width: 180 },

        { field: 'main_online_since', headerName: 'Main Online Since', width: 200 },
        { field: 'main_online_time', headerName: 'Main Online Time', width: 160 },
        { field: 'main_last_handshake', headerName: 'Main Last Handshake', width: 190 },
        { field: 'main_rx', headerName: 'Main Rx', width: 140 },
        { field: 'main_tx', headerName: 'Main Tx', width: 140 },
        { field: 'main_total', headerName: 'Main Total', width: 140 },
        { field: 'main_allowed_ips', headerName: 'Main Allowed Ips', width: 260 },
        { field: 'main_is_online', headerName: 'Main Is Online', width: 150 },

        { field: 'secondary_online_since', headerName: 'Secondary Online Since', width: 220 },
        { field: 'secondary_online_time', headerName: 'Secondary Online Time', width: 180 },
        { field: 'secondary_last_handshake', headerName: 'Secondary Last Handshake', width: 210 },
        { field: 'secondary_rx', headerName: 'Secondary Rx', width: 160 },
        { field: 'secondary_tx', headerName: 'Secondary Tx', width: 160 },
        { field: 'secondary_total', headerName: 'Secondary Total', width: 170 },
        { field: 'secondary_allowed_ips', headerName: 'Secondary Allowed Ips', width: 280 },
        { field: 'secondary_is_online', headerName: 'Secondary Is Online', width: 170 },
    ];

    const rows = useMemo(() => {
        const mergedPeers = mergePeers(peers.vpn_server_main, peers.vpn_server_secondary);

        return mergedPeers.map((item) => {
            return {
                id: item.id,
                device_id: formatText(item.main?.deviceId || item.secondary?.deviceId),
                name: formatText(item.main?.name || item.secondary?.name),

                main_online_since: formatOnlineSinceKyiv(item.main?.online_since),
                main_online_time: formatOnlineTime(item.main?.online_secs),
                main_last_handshake: formatText(item.main?.human?.last_handshake),
                main_rx: formatTraffic(item.main?.rx_bytes, item.main?.human?.rx),
                main_tx: formatTraffic(item.main?.tx_bytes, item.main?.human?.tx),
                main_total: formatTraffic(item.main?.total_transfer, item.main?.human?.total_transfer),
                main_allowed_ips: formatAllowedIps(item.main?.allowed_ips),
                main_is_online: formatBoolean(item.main?.is_online),

                secondary_online_since: formatOnlineSinceKyiv(item.secondary?.online_since),
                secondary_online_time: formatOnlineTime(item.secondary?.online_secs),
                secondary_last_handshake: formatText(item.secondary?.human?.last_handshake),
                secondary_rx: formatTraffic(item.secondary?.rx_bytes, item.secondary?.human?.rx),
                secondary_tx: formatTraffic(item.secondary?.tx_bytes, item.secondary?.human?.tx),
                secondary_total: formatTraffic(
                    item.secondary?.total_transfer,
                    item.secondary?.human?.total_transfer,
                ),
                secondary_allowed_ips: formatAllowedIps(item.secondary?.allowed_ips),
                secondary_is_online: formatBoolean(item.secondary?.is_online),
            };
        });
    }, [peers]);

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: 25,
                        page: 0,
                    },
                },
            }}
        />
    );
};
